import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, Sparkles } from "lucide-react";

const LS_KEY = "lc_tour_done_v3";

const STEPS = [
  {
    emoji: "🌸",
    title: "Welcome to MyLymphieLife",
    body: "Your private space to track symptoms, mood, progress and talk about your experience.",
  },
  {
    emoji: "📅",
    title: "Tap any day to log it",
    body: "Click a day on the calendar to open your entry. Write a journal, pick your mood, upload photos, and log self-care.",
  },
  {
    emoji: "🛡️",
    title: "Cellulitis tracker",
    body: "The red Cellulitis button opens your infection log. Tap the body diagram to mark affected areas, run a daily symptom check-in (temp, red streaks, achy…), and the stage auto-updates from early onset to severe. Days inside an active episode show red on your calendar so nothing gets missed.",
  },
  {
    emoji: "🚨",
    title: "Emergency script",
    body: "If things get scary, tap the Emergency button. A 30-second assessment builds a doctor-ready script — allergies, meds, lymphedema history and current symptoms — that you can read aloud at A&E, copy, or email your GP. The ER timeline tab tracks wait times and sepsis red flags while you're there.",
  },
  {
    emoji: "💗",
    title: "Meet Lymphie",
    body: "Your AI companion lives in the bottom-right corner. Vent, ask for lymphedema advice, or just talk. She's always here.",
  },
  {
    emoji: "📈",
    title: "See patterns & share with your doctor",
    body: "The Trends tab surfaces gentle observations. One tap exports a PDF summary for medical visits.",
  },
  {
    emoji: "⭐",
    title: "Earn Care Points",
    body: "Every self-care action — compression, MLD massage, elevation, meds, journaling — earns points. Watch them grow.",
  },
];

export default function TourOverlay() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!localStorage.getItem(LS_KEY)) {
      const t = setTimeout(() => setOpen(true), 700);
      return () => clearTimeout(t);
    }
  }, []);

  // Any component can dispatch `replay-tour` to re-open the demo
  useEffect(() => {
    const handler = () => { setStep(0); setOpen(true); };
    window.addEventListener("replay-tour", handler);
    return () => window.removeEventListener("replay-tour", handler);
  }, []);

  const finish = () => {
    localStorage.setItem(LS_KEY, "1");
    setOpen(false);
  };

  const next = () => {
    if (step === STEPS.length - 1) return finish();
    setStep(step + 1);
  };

  const current = STEPS[step];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] backdrop-blur-xl bg-white/40 flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="tour-overlay"
        >
          <motion.div
            initial={{ y: 30, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 10, opacity: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 22 }}
            className="w-full max-w-md relative rounded-[2rem] overflow-hidden border border-kawaii-border p-8 md:p-10"
            style={{ background: 'linear-gradient(135deg, #FFF9F9 0%, #FFE5E5 60%, #E2F0CB 100%)' }}
          >
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-kawaii-pink/30 blur-3xl" />
            <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-kawaii-lavender/30 blur-3xl" />

            <button
              onClick={finish}
              data-testid="tour-skip-btn"
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/70 backdrop-blur border border-white/60 flex items-center justify-center text-kawaii-textSoft"
            >
              <X className="w-4 h-4" strokeWidth={2.5} />
            </button>

            <div className="relative">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-kawaii-textSoft" strokeWidth={2.5} />
                <span className="text-[10px] uppercase tracking-[0.28em] text-kawaii-textSoft font-bold">
                  {step + 1} / {STEPS.length}
                </span>
              </div>

              <motion.div
                key={step}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
              >
                <div className="text-6xl mb-4 leading-none">{current.emoji}</div>
                <h2 className="font-heading text-2xl md:text-3xl font-semibold text-kawaii-text leading-tight" data-testid="tour-title">
                  {current.title}
                </h2>
                <p className="mt-3 text-kawaii-textSoft font-body text-base leading-relaxed" data-testid="tour-body">
                  {current.body}
                </p>
              </motion.div>

              <div className="mt-6 flex items-center gap-1.5">
                {STEPS.map((s, i) => (
                  <span
                    key={s.title}
                    className={`h-1.5 rounded-full transition-all ${i === step ? 'w-8 bg-kawaii-pink' : 'w-1.5 bg-kawaii-border'}`}
                  />
                ))}
              </div>

              <div className="mt-6 flex items-center gap-3">
                <button
                  onClick={finish}
                  data-testid="tour-skip-link"
                  className="text-sm text-kawaii-textSoft font-body hover:text-kawaii-text"
                >
                  Skip tour
                </button>
                <button
                  onClick={next}
                  data-testid="tour-next-btn"
                  className="kawaii-btn ml-auto px-6 py-2.5 rounded-full bg-gradient-to-r from-kawaii-pink to-kawaii-peach text-white font-heading font-semibold text-sm shadow-md inline-flex items-center gap-2"
                >
                  {step === STEPS.length - 1 ? "Let's begin ♡" : "Next"}
                  {step < STEPS.length - 1 && <ChevronRight className="w-4 h-4" strokeWidth={2.5} />}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
